import { formatUsd } from '@/lib/format';

/**
 * What booking a returned / disputed payment will do (the preview) and did
 * (the result), shared by the customer Billing tab and the payment ticket.
 * Backed by admin-record-returned-payment → qbo-returned-payment.
 */

export interface ReturnPlan {
  channel: 'bank' | 'stripe';
  payment: { id: string; total: number; txn_date: string; unapplied: number };
  reopens: Array<{ id: string; doc_number: string; total: number; applied: number }>;
  via_invoice: { id: string; doc_number: string } | null;
  expense?: {
    bank_account: { id: string; name: string } | null;
    ar_account: { id: string; name: string } | null;
    amount: number;
    txn_date?: string;
  };
  fee: { amount: number; item: string; label?: string; account?: { id: string; name: string } | null } | null;
}

export interface ReturnResult {
  channel: 'bank' | 'stripe';
  reopened: Array<{ id: string; doc_number: string; applied: number }>;
  voided_payment_id: string | null;
  returned_expense_id: string | null;
  payment_total: number;
  fee_invoice: { id: string; doc_number: string } | null;
  hold_applied: boolean;
  notified: string[];
  notify_error: string | null;
  payouts?: Array<{ payout_id: string; status: string; reason?: string }>;
  note: string;
}

export function ReturnPlanView({
  plan,
  pauseOrdering,
  notifyCustomer,
}: {
  plan: ReturnPlan;
  pauseOrdering: boolean;
  notifyCustomer: boolean;
}) {
  const stripe = plan.channel === 'stripe';
  return (
    <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-4 py-3 text-xs text-brix-ink space-y-1.5">
      <div className="font-semibold">This will:</div>
      <div>
        1. Unapply QuickBooks payment <span className="font-mono">{plan.payment.id}</span> ({formatUsd(plan.payment.total)},
        dated {plan.payment.txn_date}) from{' '}
        {plan.reopens.length === 1 ? 'the invoice it paid, which reopens:' : `all ${plan.reopens.length} invoices it paid, which reopen:`}
        <ul className="mt-1 ml-4 list-disc">
          {plan.reopens.map((i) => (
            <li key={i.id}>
              <span className="font-mono">#{i.doc_number}</span> — {formatUsd(i.applied)}
            </li>
          ))}
          {plan.payment.unapplied > 0 && <li>{formatUsd(plan.payment.unapplied)} that sat as credit on the account</li>}
        </ul>
      </div>
      <div>
        2. Book the {formatUsd(plan.payment.total)} clawback against the customer’s balance, paid from{' '}
        <strong>{plan.expense?.bank_account?.name ?? 'the deposit bank account'}</strong>.{' '}
        {stripe
          ? 'Stripe took it out of a payout, so the payout that netted it then posts by itself with a matching line on that account (plus the Stripe dispute fee).'
          : 'This is what the clawback line in the bank feed matches to.'}{' '}
        The original deposit stays reconciled.
      </div>
      {plan.fee ? (
        <div>
          3. Create a {formatUsd(plan.fee.amount)} “{plan.fee.label ?? plan.fee.item}” invoice, due on receipt,
          booked to <strong>{plan.fee.account?.name ?? 'bank fees'}</strong>. The invoice spells out the payment, its date,
          the reason and every invoice that reopened.
        </div>
      ) : (
        <div>3. No fee will be charged.</div>
      )}
      <div>
        4. {pauseOrdering ? 'Pause ordering on this account.' : 'Leave ordering enabled.'}{' '}
        {notifyCustomer ? 'Email the customer’s invoice contacts.' : 'No customer email.'}
      </div>
    </div>
  );
}

export function ReturnResultView({ result }: { result: ReturnResult }) {
  const posted = result.payouts?.filter((p) => p.status === 'posted') ?? [];
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-900 space-y-1">
      <div className="font-semibold">Booked in QuickBooks.</div>
      <div>
        Payment {result.voided_payment_id} ({formatUsd(result.payment_total)}) is unapplied —{' '}
        {result.reopened.length > 0
          ? `invoice${result.reopened.length === 1 ? '' : 's'} ${result.reopened.map((r) => `#${r.doc_number}`).join(', ')} ${result.reopened.length === 1 ? 'is' : 'are'} unpaid again.`
          : 'the invoice is unpaid again.'}
      </div>
      {result.fee_invoice && <div>Fee invoice #{result.fee_invoice.doc_number} created.</div>}
      {result.hold_applied && <div>Ordering paused on this account.</div>}
      {posted.length > 0 && (
        <div>Held Stripe payout{posted.length === 1 ? '' : 's'} posted: {posted.map((p) => p.payout_id).join(', ')}.</div>
      )}
      <div>
        {result.notified.length > 0
          ? `Customer notified: ${result.notified.join(', ')}`
          : result.notify_error
            ? `Customer NOT notified — ${result.notify_error}`
            : 'Customer not notified.'}
      </div>
      <div className="text-emerald-700">{result.note}</div>
    </div>
  );
}
